// FP 자원 간트 — 하단 상세탭 ③ PSI
//
// old: `simulation/adjustmentgrid3/PsiTab.jsx`(ResourceGantt와 AdjustmentGrid3가 공유하던 컴포넌트)
// API: `POST factoryplan/analysis/prod-sales-stock-status`(이관 완료). new에 같은 크로스탭 화면
//      `report/ProdSalesStockStatus.js`가 이미 있어 **컬럼/행 조립 규칙을 그대로 따른다**
//      (고정 컬럼 + `buildDayBucketPivotColumnDefs` + `pivotData[i] → crosstabHeader[i]` 펼치기).
//
// **선택한 바의 품목 PSI만 본다.** old가 넘기던 파라미터(`planScope`/`versionCd`/`plantCd`/`itemCd`
// + `usePlanPeriod:true` + `bucketType:'BUCKET_DAY'` 고정)를 그대로 쓴다 — 백엔드
// `ProdSalesStockStatusQueryRepository.makeWhereClause:504,506`이 `plantCd`/`itemCd` 단건 필터를
// 지원하는 것을 확인했다.
//
// old의 `activity.Col === 'GANTT'`일 때 `JSON.parse(activity.activityData)` 우회는 **이관하지 않는다** —
// TreeGrid RunBox에서 활동을 되꺼내는 코드였고, new는 `box.activity`가 이미 객체다.
//
// old `lastFetchedKeyRef`(같은 활동 재조회 억제)는 유지한다 — 탭을 왕복할 때마다 같은 조회가 나가면
// 크로스탭은 비용이 크다.
//
// ⚠️ `viewName`을 `ProdSalesStockStatus`와 **다르게** 받아야 한다 — 같은 키를 쓰면 그 화면과 컬럼
//    p13n 상태가 섞인다(호출부에서 `${viewName}_PSI`를 넘긴다).
//
// **공장·스테이지·품목은 컬럼이 아니라 문맥 밴드다**(2026-08-24 2차 지시, §7-23·§7-25).
// 조회가 **선택한 활동의 `plantCd` + `itemCd` 단건 필터**라 그 셋이 전 행 동일하다 —
// 생산 계획 상세 탭과 같은 이유로 그리드에서 빼고 `DetailContextBand`에 얹었다.
// 남은 고정 컬럼은 **실제로 값이 갈릴 수 있는 것**뿐이다: 인벤토리 코드 / 주문 품목(둘 다 동일값
// 연속 병합) + 분석 지표(병합하지 않는다 — 같은 값이 기초/기말처럼 떨어져 재등장한다).
// 행 그룹(트리)은 전부 걷어냈다 → 평평한 그리드.
//
// 아래 트리 관련 기록은 **그 이전 단계**의 경과다(왜 트리로 갔다가 되돌렸는지):
// **행 그룹 트리 이관됨** (2026-08-24 사용자 지시). old mergeRule(`prevvalues + value`)의 세로 병합은
// 트리 + `groupHideOpenParents`가 같은 모양으로 대체한다(다른 FP Report 화면 19개와 동일 패턴).
// old `setFixedOptions({colCount:4})`(좌측 고정)은 `pinned:"left"`로 대응한다.
//
// 계층은 **공장 > 스테이지 > 품목**이고, 품목 단이 old `itemGroup`과 같이 **컬럼 4개**를 갖는다
// (2026-08-24 2차 지시). 처음엔 인벤토리를 leaf로 뒀는데, old는 그 둘도 `itemGroup` 안이었다:
//
// | old `itemGroup` 자식 | groupShowMode | 여기 |
// |---|---|---|
// | `itemCd` (품목 코드) | always | **항상 보임** — 이 컬럼이 화살표를 갖는다 |
// | `itemNm` (품목 명칭) | expand | 화살표로 여닫음 |
// | `inventoryCd` (인벤토리 코드) | expand | 화살표로 여닫음 |
// | `inventoryNm` (**주문 품목** — `FP_INVENTORY_NM`의 한국어 라벨) | expand | 화살표로 여닫음 |
//
// 네 컬럼 **모두 트리 레벨**이고(`showRowGroup`), 토글은 **하나**다 — `buildGroupColumnPair`의
// `extraDetailColumns`로 인벤토리 두 컬럼을 품목 코드의 화살표에 묶었다(grid.js:211 용법,
// `PsiDetail.js`가 품목구분을 인벤토리 화살표에 묶은 것과 같다).
//
// ⚠️ **AG Grid 컬럼 그룹(`children`) 헤더는 만들지 않았다** — 공장·스테이지도 그룹 헤더가 없고
//    (이 코드베이스의 Report 패턴이 old의 그룹 헤더를 `CustomDetailHeader` 화살표로 대체했다),
//    품목만 그룹 헤더를 달면 헤더 행이 어긋난다. "품목 그룹"은 화살표 하나로 함께 여닫히는
//    이 네 컬럼을 뜻한다.

import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";
import { green } from "@mui/material/colors";

import fpProdSalesStockStatusService from "@plannel/services/fp/fp-prod-sales-stock-status-service";
import FpGridSetting, { buildDayBucketPivotColumnDefs } from "@plannel/pages/factory-plan/common/grid";
import DetailContextBand from "@plannel/pages/factory-plan/report/resource-gantt/DetailContextBand";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";

// old PsiTab의 `bucketType: 'BUCKET_DAY'` 고정값. 이 탭에는 버킷 선택 UI가 없다(old도 없었다).
const BUCKET_TYPE = "BUCKET_DAY";
const FP_ENDING_STOCK = "FP_ENDING_STOCK";

// old fixColumns 중 이 탭에서 의미가 있는 것만 남긴다 — 단일 품목 조회라 품목/인벤토리/지표가 핵심이다.
const FIXED_FIELDS = ["plantCd", "plantNm", "stageCd", "stageNm", "itemCd", "itemNm", "inventoryCd", "inventoryNm", "analysisIndicator"];


const PsiTab = ({ active, activity, versionState, viewName, t }) => {
  const gridRef = useRef(null);
  const [rowData, setRowData] = useState([]);
  const [pivotColumnDefs, setPivotColumnDefs] = useState([]);
  // 마지막으로 조회한 활동 키. 같으면 다시 조회하지 않는다(old lastFetchedKeyRef).
  const lastFetchedKeyRef = useRef(null);
  const latestReqRef = useRef(0);

  // old styleCallback — 기말 재고 행은 배경 강조(ProdSalesStockStatus.js와 동일 기준·색).
  const endingStockStyle = (params) => (
    params.data?.analysisIndicator === FP_ENDING_STOCK ? { backgroundColor: green[50] } : null
  );

  const fixedColumnDefs = useMemo(() => [
    // **고정 컬럼은 분석 지표 하나뿐이다.** 공장·스테이지·품목·인벤토리는 전부 문맥 밴드로 나갔다
    // (파일 머리말) — 조회가 단건 필터라 전 행 동일하기 때문이다.
    //
    // 분석 지표는 **병합하지 않는다** — 같은 값(예: 재고)이 기초/기말처럼 떨어진 위치에 다시
    // 나타나야 하는데 병합·그룹은 순서를 무너뜨린다(`PsiDetail.js:263`과 같은 이유).
    // old displayCallback: transLangKey(value) — 값 자체가 다국어 키(FP_BEGINNING_STOCK 등)다.
    {
      headerName: t("FP_ANALYSIS_INDICATOR"), field: "analysisIndicator", width: 130,
      pinned: "left", lockPinned: true,
      valueFormatter: (params) => (params.value ? t(params.value) : ""),
      cellStyle: endingStockStyle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t]);

  const gridOptions = useMemo(() => {
    const base = FpGridSetting({ title: "PSI", viewName });
    return {
      ...base,
      // **행 그룹(트리)·셀 병합 옵션이 모두 없다** — 문맥이 전부 밴드로 나가서 남은 고정 컬럼이
      // 분석 지표 하나뿐이고, 그건 병합 대상이 아니다. 평평한 크로스탭 그리드다.
      defaultColDef: { ...base.defaultColDef, editable: false, resizable: true }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // old setCrossTabGridDataWithBucket — pivotData[i]를 crosstabHeader[i] 필드로 펼친다
  // (ProdSalesStockStatus.js:276의 buildRowData와 같은 규칙).
  const buildRowData = (crosstabHeader, data) => (data ?? []).map(row => {
    const flat = {};
    FIXED_FIELDS.forEach(field => { flat[field] = row[field]; });
    (crosstabHeader ?? []).forEach((header, index) => { flat[header] = row.pivotData?.[index]; });
    return flat;
  });

  // 이 탭만 **백엔드를 직접 조회**한다(나머지 두 탭은 간트 본조회 결과를 넘겨받는다) — 그래서
  // 진행 표시가 필요하다. FP 관용구는 AG Grid 내장 오버레이를 `setGridOption`으로 켜는 것이다
  // (`simulation/AdjustmentGrid.js:872,893,1165,1199`) — 별도 state를 만들지 않아 재렌더가 없다.
  const setGridLoading = (on) => gridRef.current?.api?.setGridOption("loading", on);

  useEffect(() => {
    // 탭이 보이지 않으면 조회하지 않는다(old도 `props.tab !== props.id`면 즉시 return).
    if (!active) return;

    const target = activity;
    const key = target ? `${target.plantCd}|${target.itemCd}|${versionState?.versionCd}` : null;

    if (!key || !versionState?.planScope || !versionState?.versionCd) {
      setRowData([]);
      setPivotColumnDefs([]);
      setGridLoading(false);
      lastFetchedKeyRef.current = null;
      return;
    }
    if (lastFetchedKeyRef.current === key) return;
    lastFetchedKeyRef.current = key;

    const reqId = ++latestReqRef.current;
    setGridLoading(true);
    fpProdSalesStockStatusService.getProdSalesStockStatuses({
      viewId: viewName,
      subId: "",
      planScope: versionState.planScope,
      versionCd: versionState.versionCd,
      plantCd: target.plantCd,
      itemCd: target.itemCd,
      // old는 fromDt/toDt를 주석 처리하고 usePlanPeriod:true만 보냈다 — 계획기간 전체를 본다.
      usePlanPeriod: true,
      bucketType: BUCKET_TYPE
    })
      .then(response => {
        // 늦게 도착한 응답은 버린다 — 다만 **로딩은 최신 요청만 끈다**(여기서 끄면 뒤늦은 응답이
        // 진행 중인 조회의 표시를 지운다).
        if (reqId !== latestReqRef.current) return;

        const { crosstabHeader, data } = response.data ?? {};
        setPivotColumnDefs(buildDayBucketPivotColumnDefs(crosstabHeader, t, {
          childExtraProps: () => ({ cellStyle: endingStockStyle })
        }));
        setRowData(buildRowData(crosstabHeader, data));
        setGridLoading(false);
      })
      .catch(e => {
        if (reqId === latestReqRef.current) {
          setRowData([]);
          setPivotColumnDefs([]);
          setGridLoading(false);
        }
        debug(e);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, activity?.plantCd, activity?.itemCd, versionState?.planScope, versionState?.versionCd, viewName]);

  // 조회할 때마다 피벗 컬럼 구성 자체가 바뀌므로, 컬럼정의는 응답 시점에 조립해 그리드에 적용한다
  // (다른 크로스탭 Report 화면과 같은 흐름 — 헤더 번역은 위에서 t()로 이미 끝냈다).
  const columnDefs = useMemo(() => [...fixedColumnDefs, ...pivotColumnDefs], [fixedColumnDefs, pivotColumnDefs]);

  useEffect(() => {
    const api = gridRef.current?.api;
    if (!api) return;
    GridUtils.setColumnDefs({ api, columnDefs, viewName, initState: false });
  }, [columnDefs, viewName]);

  // 문맥은 전 행 동일하므로 첫 행에서 읽는다(조회가 `plantCd`+`itemCd` 단건 필터라 그렇다).
  const context = rowData?.[0];

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      {context ? (
        /* 건수는 두지 않는다(2026-08-24 사용자 지시) — 여기 행 수는 **분석 지표 개수**라
           업무적으로 읽을 값이 아니다(생산 계획 상세의 "작업 N건"과 성격이 다르다). */
        <DetailContextBand
          items={[
            { labelKey: "FP_PLANT", code: context.plantCd, name: context.plantNm },
            { labelKey: "FP_STAGE", code: context.stageCd, name: context.stageNm },
            { labelKey: "FP_ITEM", code: context.itemCd, name: context.itemNm },
            // 라벨은 `FP_INVENTORY`("인벤토리") — 공장/스테이지/품목과 같은 **개체명** 계열이다.
            // `FP_INVENTORY_CD`("인벤토리 코드")를 쓰면 "인벤토리 코드 INV#01 (…)"로 코드가 겹친다.
            { labelKey: "FP_INVENTORY", code: context.inventoryCd, name: context.inventoryNm }
          ]}
          t={t}
        />
      ) : null}

      {/* 하단 상태바(Total Rows)를 뺐다 — 건수는 위 밴드가 문맥과 함께 보여준다(2026-08-24 사용자 지시). */}
      <Box className="ag-theme-balham grid-area" sx={{ flex: "1 1 0", minHeight: 0 }}>
        <AgGridReact
          ref={gridRef}
          rowData={rowData}
          {...gridOptions}
        />
      </Box>
    </Box>
  );
};

export default PsiTab;
